import {
  Button,
  Table,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Box,
  Flex,
  Spacer,
  Text,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  RowSelectionState,
  ColumnDef,
  Row,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util, CreditAgreement } from '@finley-technologies/finley-types';

import {
  LoanProductGQLType,
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableIdGroupWithLoanProductGQLType,
  VerificationEligibilityReceivableGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  ColumnFilter,
  ReceivableEligibilitySelectActionRow,
  renderSelectCell,
  renderWaivedIconCell,
  VerificationReceivableEligibilityColumnSelector,
} from './VerificationReceivableEligibilityTableComponents';
import {
  createBasefieldsColumns,
  createInitialColumnVisibility,
  getColumnStylesMap,
  formatIndividualEligibilityCriteriaColumnDef,
  formatReceivableEligibilityDifferenceColumnDef,
  individualEligibilityCriteriaColumnHeaders,
  receivableIdColumnHeader,
  stickyColumnWidthPx,
  numberToPxStr,
  mainHeaderZIndex,
  createReceivableEligibilityData,
  parseEligibilityTestsFormulasAndBasefieldsFromCreditAgreement,
  ReceivableEligibilityDifferencesSelectActions,
  ColumnStyle,
} from './VerificationReceivableEligibilityTableUtils';
import { VerificationReceivableEligibilityUpdateWaiversDrawerContainer } from './VerificationReceivableEligibilityUpdateWaiversDrawer';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { colors } from 'packages/common-frontend/colors';
import { LoanProductSelect } from 'packages/common-frontend/components/LoanProductSelect';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import {
  getMockUiVisibilityMode,
  useCreditFacilityUiVisibility,
} from 'packages/common-frontend/hooks/useUiVisibility';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

type TableCellProps = {
  index: number;
  rowSpan: number;
  borderTopWidth: string;
  borderBottom: string;
  columnId: string;
  children: React.ReactElement;
  columnStylesMap: ColumnStyle[];
};
const TableCell = ({
  index,
  rowSpan,
  borderTopWidth,
  borderBottom,
  columnId,
  children,
  columnStylesMap,
}: TableCellProps) => {
  return (
    <Td
      position={columnStylesMap[index] ? 'sticky' : undefined}
      backgroundColor="white !important"
      style={{
        ...columnStylesMap[index],
        zIndex: mainHeaderZIndex,
      }}
      // rowSpan for merged cells at the receivable level has been already calculated
      // cells at the individual eligibility criterial level should all be rendered with rowSpan of 1
      rowSpan={
        _.includes(individualEligibilityCriteriaColumnHeaders, columnId)
          ? 1
          : rowSpan
      }
      fontSize="sm"
      padding="16px"
      borderTopWidth={borderTopWidth}
      borderBottom={borderBottom}
      verticalAlign="top"
    >
      {children}
    </Td>
  );
};

type VerificationReceivableEligibilityTableProps = {
  receivableEligibility: VerificationEligibilityReceivableGQLType[];
  eligibilityFormulasAndBasefields: {
    formulas: { [key: string]: string };
    basefields: string[];
  } | null;
  selectedLoanProductId: string;
  isWaiveDisabled: boolean;
  showSelectMode: boolean;
  onExitSelectMode: () => void;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  loanProductReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
  receivableWaiversForLoanProduct: ReceivableBorrowingBaseTestWaiverGQLType[];
  borrowerOrganizationName: string;
};
const VerificationReceivableEligibilityTable = ({
  receivableEligibility,
  eligibilityFormulasAndBasefields,
  selectedLoanProductId,
  isWaiveDisabled,
  showSelectMode,
  onExitSelectMode,
  creditFacilityId,
  reportingDate,
  loanProductReceivableIdGroups,
  receivableWaiversForLoanProduct,
  borrowerOrganizationName,
}: VerificationReceivableEligibilityTableProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [basefieldColumns, setBasefieldColumns] = useState<
    ColumnDef<VerificationEligibilityReceivableGQLType, unknown>[]
  >(() => []);

  // tanstack table column state mgmt
  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelectionState, setRowSelectionState] = useState<RowSelectionState>(
    {},
  );
  const [
    selectedAction,
    setSelectedAction,
  ] = useState<ReceivableEligibilityDifferencesSelectActions>('updateWaivers');

  useEffect(() => {
    const basefieldsColumns = createBasefieldsColumns(receivableEligibility);
    const initialColumnVisibility = createInitialColumnVisibility({
      verificationEligibilityReceivables: receivableEligibility,
      eligibilityRelatedBasefields:
        eligibilityFormulasAndBasefields?.basefields,
    });

    setBasefieldColumns(basefieldsColumns);
    setColumnVisibility(initialColumnVisibility);
    setSorting([]);
    setColumnFilters([]);
  }, [
    selectedLoanProductId,
    receivableEligibility,
    eligibilityFormulasAndBasefields,
  ]);

  const getSelectColumnDefOrUndefined = () => {
    if (showSelectMode) {
      return [
        {
          id: 'select',
          cell: ({
            row,
          }: {
            row: Row<VerificationEligibilityReceivableGQLType>;
          }) => renderSelectCell({ row, isDisabled: isWaiveDisabled }),
        },
      ];
    }

    return [];
  };

  const table = useReactTable({
    data: receivableEligibility,
    columns: [
      ...getSelectColumnDefOrUndefined(),
      formatReceivableEligibilityDifferenceColumnDef(receivableIdColumnHeader),
      {
        id: 'waived',
        accessorKey: 'waived',
        cell: ({ row, column }) =>
          renderWaivedIconCell({
            row,
            column,
          }),
        enableSorting: true,
        enableColumnFilter: false,
      },
      formatReceivableEligibilityDifferenceColumnDef('exposure'),
      formatReceivableEligibilityDifferenceColumnDef('eligibleExposure'),
      formatIndividualEligibilityCriteriaColumnDef({
        accessorKey: 'eligibilityCriteriaKey',
        enableColumnFilter: true,
        enableSorting: true,
      }),
      formatIndividualEligibilityCriteriaColumnDef({
        accessorKey: 'borrowerEligible',
        header: 'Borrower Eligibility Status',
        enableColumnFilter: true,
        enableSorting: true,
      }),
      formatIndividualEligibilityCriteriaColumnDef({
        accessorKey: 'finleyEligibilityStatus',
        enableColumnFilter: true,
        enableSorting: true,
      }),
      formatIndividualEligibilityCriteriaColumnDef({
        accessorKey: 'borrowerFormula',
        enableColumnFilter: false,
        enableSorting: false,
      }),
      formatIndividualEligibilityCriteriaColumnDef({
        accessorKey: 'finleyFormula',
        enableColumnFilter: false,
        enableSorting: false,
      }),
      {
        id: 'finleyReceivableId',
        accessorKey: 'finleyReceivableId',
      },
      ...basefieldColumns,
    ],
    state: {
      columnVisibility,
      sorting,
      columnFilters,
      rowSelection: rowSelectionState,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelectionState,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
    enableRowSelection: true,
    enableMultiRowSelection: true,
  });

  const renderedReceivableIds: { [key: string]: string } = {};

  const columnStylesMap = getColumnStylesMap({ showSelectMode });
  const selectedReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[] =
    table.getSelectedRowModel().rows.map((row) => ({
      finleyReceivableId: row.getValue('finleyReceivableId'),
      externalReceivableId: row.getValue('externalReceivableId'),
      loanProductId: selectedLoanProductId,
    })) ?? [];

  return (
    <Box>
      {Boolean(selectedReceivableIdGroups.length) && (
        <VerificationReceivableEligibilityUpdateWaiversDrawerContainer
          isOpen={isOpen}
          onClose={onClose}
          creditFacilityId={creditFacilityId}
          loanProductId={selectedLoanProductId}
          reportingDate={reportingDate}
          loanProductReceivableIdGroups={loanProductReceivableIdGroups}
          selectedReceivableIdGroups={selectedReceivableIdGroups}
          receivableWaiversForLoanProduct={receivableWaiversForLoanProduct}
          borrowerOrganizationName={borrowerOrganizationName}
        />
      )}
      <VerificationReceivableEligibilityColumnSelector
        tableInstance={table}
        verificationEligibilityReceivables={receivableEligibility}
      />
      <Box
        borderWidth="medium"
        borderColor={colors.gray[200]}
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="70vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
        mt="16px"
      >
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            {_.map(table.getHeaderGroups(), (headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const columnCanSort = header.column.getCanSort();
                  const columnCanFilter = header.column.getCanFilter();
                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      textTransform="capitalize"
                      padding="8px 16px 8px 16px"
                      minWidth={numberToPxStr(stickyColumnWidthPx)}
                      position="sticky"
                      top="0"
                      borderBottomWidth="thin"
                      zIndex={mainHeaderZIndex}
                      style={columnStylesMap[index]}
                    >
                      {header.isPlaceholder ? null : (
                        <Box>
                          <Flex alignItems="center">
                            <Box
                              cursor={columnCanSort ? 'pointer' : 'default'}
                              onClick={header.column.getToggleSortingHandler()}
                              mr="8px"
                            >
                              {{
                                asc: <FaSortUp />,
                                desc: <FaSortDown />,
                              }[header.column.getIsSorted() as string] ??
                                (columnCanSort ? <FaSort /> : null)}
                            </Box>

                            <Box mr="8px">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </Box>

                            {columnCanFilter && (
                              <ColumnFilter
                                column={header.column}
                                currentColumnFilters={columnFilters}
                              />
                            )}
                          </Flex>
                        </Box>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {showSelectMode && (
              <ReceivableEligibilitySelectActionRow
                selectedAction={selectedAction}
                setSelectedAction={setSelectedAction}
                onExitSelectMode={onExitSelectMode}
                colSpan={table.getAllColumns().length}
                onOpenSelectActionDrawer={onOpen}
                areReceivableIdsSelected={Boolean(
                  selectedReceivableIdGroups.length,
                )}
              />
            )}
            {_.map(table.getRowModel().rows, (row) => {
              // rowSpan for cells in this row component which are per-receivable basis
              // this is all columns except for columns that are on a per-criteria basis
              // as noted in individualEligibilityCriteriaColumnHeaders
              let rowSpan = 1;
              let renderSharedCellsInRow = true;
              const receivableIdForRow: string = row.getValue(
                receivableIdColumnHeader,
              );
              const visibleFilteredrows = table.getFilteredRowModel().rows;

              // renderedReceivableIds is a const {} that resets per render
              // the existence of a receivableId inside renderedReceivableIds means that the main merged row
              // for that receivable has already been rendered (with enough rowSpan to cover every eligibility criteria row)
              // so we should not render that cell again
              if (!renderedReceivableIds[receivableIdForRow]) {
                renderedReceivableIds[receivableIdForRow] = receivableIdForRow;
                const rowsWithReceivableId = _.filter(
                  visibleFilteredrows,
                  (filteredRow) =>
                    filteredRow.original.externalReceivableId ===
                    receivableIdForRow,
                ).length;

                rowSpan = rowsWithReceivableId;
              } else {
                renderSharedCellsInRow = false;
              }

              const eligibilityCriteriaKeyForRow = row.getValue(
                'eligibilityCriteriaKey',
              );

              return (
                <Tr key={row.id}>
                  {_.map(row.getVisibleCells(), (cell, index) => {
                    const cellValue = cell.getValue();
                    let formattedCellValue = cell.column.columnDef.cell;
                    let borderTopWidth = 'medium';
                    let borderBottom = 'thin';

                    switch (cell.column.id) {
                      case 'exposure':
                      case 'eligibleExposure':
                        if (typeof cellValue === 'number') {
                          formattedCellValue = commonUtils.numberToCurrencyWithSymbol(
                            {
                              number: cellValue,
                            },
                          );
                        }
                        break;
                      case 'borrowerEligible':
                        formattedCellValue = cellValue
                          ? 'Eligible'
                          : 'Ineligible';
                        break;
                      case 'eligibilityCriteriaKey':
                      case 'finleyEligibilityStatus':
                        if (typeof cellValue === 'string') {
                          formattedCellValue = commonUtils.toTitleCase(
                            cellValue,
                          );
                        }
                        break;

                      default:
                        formattedCellValue = cell.column.columnDef.cell;
                    }

                    // this determines row borders
                    // we want thinner row border between multiple eligibility criteria within the same receivable
                    switch (cell.column.id) {
                      case 'borrowerEligible':
                      case 'eligibilityCriteriaKey':
                      case 'finleyEligibilityStatus':
                      case 'borrowerFormula':
                        if (!renderSharedCellsInRow) {
                          borderTopWidth = 'thin';
                        }
                        if (rowSpan > 1 && renderSharedCellsInRow) {
                          borderBottom = 'none';
                        }

                        break;

                      default:
                        borderTopWidth = 'medium';
                    }

                    const shouldRenderCell =
                      renderSharedCellsInRow ||
                      _.includes(
                        individualEligibilityCriteriaColumnHeaders,
                        cell.column.id,
                      );

                    const isBorrowerFormulaCell =
                      cell.column.id === 'borrowerFormula';
                    const isFinleyFormulaCell =
                      cell.column.id === 'finleyFormula';

                    return (
                      shouldRenderCell &&
                      (() => {
                        if (isBorrowerFormulaCell) {
                          return (
                            <TableCell
                              key={cell.id}
                              index={index}
                              rowSpan={rowSpan}
                              borderTopWidth={borderTopWidth}
                              borderBottom={borderBottom}
                              columnId={cell.column.id}
                              columnStylesMap={columnStylesMap}
                            >
                              {cellValue ? (
                                <Box
                                  cursor="default"
                                  backgroundColor={colors.gray[200]}
                                  padding="8px 16px"
                                  borderRadius="8px"
                                >
                                  <Text as="pre">
                                    {flexRender(
                                      formattedCellValue,
                                      cell.getContext(),
                                    )}
                                  </Text>
                                </Box>
                              ) : (
                                <Text>-</Text>
                              )}
                            </TableCell>
                          );
                        }
                        if (isFinleyFormulaCell) {
                          return (
                            <TableCell
                              key={cell.id}
                              index={index}
                              rowSpan={rowSpan}
                              borderTopWidth={borderTopWidth}
                              borderBottom={borderBottom}
                              columnId={cell.column.id}
                              columnStylesMap={columnStylesMap}
                            >
                              {eligibilityCriteriaKeyForRow ? (
                                <Box
                                  cursor="default"
                                  backgroundColor={colors.gray[200]}
                                  padding="8px 16px"
                                  borderRadius="8px"
                                >
                                  <Text as="pre">
                                    {eligibilityFormulasAndBasefields?.formulas &&
                                    typeof eligibilityCriteriaKeyForRow ===
                                      'string'
                                      ? eligibilityFormulasAndBasefields
                                          .formulas[
                                          eligibilityCriteriaKeyForRow
                                        ]
                                      : null}
                                  </Text>
                                </Box>
                              ) : (
                                <Text>-</Text>
                              )}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            key={cell.id}
                            index={index}
                            rowSpan={rowSpan}
                            borderTopWidth={borderTopWidth}
                            borderBottom={borderBottom}
                            columnId={cell.column.id}
                            columnStylesMap={columnStylesMap}
                          >
                            <Box>
                              {flexRender(
                                formattedCellValue,
                                cell.getContext(),
                              )}
                            </Box>
                          </TableCell>
                        );
                      })()
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

type VerificationReceivableEligibilityTableContainerProps = {
  reportingDate: Util.FullDateStr;
  receivableEligibility: VerificationEligibilityReceivableGQLType[];
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  recalculate: () => Promise<void>;
  creditFacilityId: string;
  loanProducts: LoanProductGQLType[];
  receivableIdGroupsByLoanProduct: {
    [loanProductId: string]: ReceivableIdGroupWithLoanProductGQLType[];
  };
  receivableWaiversByLoanProduct: {
    [loanProductId: string]: ReceivableBorrowingBaseTestWaiverGQLType[];
  };
};
export const VerificationReceivableEligibilityTableContainer = ({
  reportingDate,
  receivableEligibility,
  creditAgreement,
  recalculate,
  creditFacilityId,
  loanProducts,
  receivableIdGroupsByLoanProduct,
  receivableWaiversByLoanProduct,
}: VerificationReceivableEligibilityTableContainerProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const { navigator } = useVerificationOptions();

  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const mockUiVisibilityMode = getMockUiVisibilityMode({
    flags,
    defaultTo: 'lenderOwnerWithoutBorrower',
  });
  const facilityUiVisibility = useCreditFacilityUiVisibility({
    mockUiVisibilityMode,
    creditFacilityId,
  });

  const initDefaultDataForAllLoanProducts = useMemo(() => {
    const defaultDataForAllLoanProducts: {
      [loanProductId: string]: VerificationEligibilityReceivableGQLType[];
    } = {};

    _.forEach(loanProducts, (loanProduct) => {
      const receivableEligibilityForSelectedLoanProduct = _.filter(
        receivableEligibility,
        (receivable) => receivable.loanProductId === loanProduct.id,
      );
      defaultDataForAllLoanProducts[loanProduct.id] = [
        ...createReceivableEligibilityData(
          receivableEligibilityForSelectedLoanProduct,
        ),
      ];
    });

    return defaultDataForAllLoanProducts;
  }, [loanProducts, receivableEligibility]);

  const [dataForAllLoanProducts] = useState(initDefaultDataForAllLoanProducts);
  const [isRecalculationg, setIsRecalculating] = useState(false);
  const [selectedLoanProductId, setSelectedLoanProductId] = useState<string>(
    loanProducts[0].id,
  );
  const [showSelectMode, setShowSelectMode] = useState<boolean>(false); // show select receivables mode

  const onRecalculate = useCallback(async () => {
    try {
      setIsRecalculating(true);
      await recalculate();

      navigator(navigate).reset();
      toast({
        ...successToastConfig,
        id: 'successRecalculateVerificationStart',
        title: 'Verification recalculation successfully started.',
      });
    } catch (err: any) {
      toast({
        ...errorToastConfig,
        id: 'errorRecalculateVerificationStart',
        title: 'Error starting recalculation.',
      });
    } finally {
      setIsRecalculating(false);
    }
  }, [recalculate, navigate, toast, navigator]);

  const onUpdateShowSelectMode = useCallback(() => {
    setShowSelectMode(!showSelectMode);
  }, [showSelectMode]);

  const eligibilityFormulasAndBasefieldsForSelectedLoanProduct = parseEligibilityTestsFormulasAndBasefieldsFromCreditAgreement(
    {
      creditAgreementDocument: creditAgreement,
      receivableEligibility: dataForAllLoanProducts[selectedLoanProductId],
    },
  );

  const showWaiveReceivableEligibilityActions =
    facilityUiVisibility?.verification.showWaiveReceivableEligibilityActions;

  const allReceivableEligibilityDifferencesForLp =
    dataForAllLoanProducts[selectedLoanProductId];

  // we currently want to ignore eligibility differences where finley's calculation is INELIGIBLE_WAIVED and borrower calc is ELIGIBLE
  // we want to still record these during verification report creations as differences
  // but we will filter the display for now on FE to not show these entries
  const filteredReceivableEligibilityDifferencesForLp = _.filter(
    allReceivableEligibilityDifferencesForLp,
    (differenceEntry) => {
      if (
        differenceEntry.finleyEligibilityStatus === 'INELIGIBLE_WAIVED' &&
        differenceEntry.borrowerEligible === true
      ) {
        return false;
      }
      return true;
    },
  );

  const borrowerOrganizationName =
    creditAgreement.facilityMechanics.parties.borrowers[0].entity.name; // only one borrower per credit agreement

  return (
    <Box>
      <Flex mb="32px">
        <LoanProductSelect
          creditFacilityId={creditFacilityId}
          loanProductId={selectedLoanProductId}
          setLoanProductId={setSelectedLoanProductId}
        />
        <Spacer />
        <Flex alignItems="center">
          <Text color={colors.gray[500]}>Reporting Date: {reportingDate}</Text>
          {showWaiveReceivableEligibilityActions && (
            <>
              <Button
                variant="outline"
                colorScheme="aurora"
                onClick={onUpdateShowSelectMode}
                mx="32px"
                borderWidth="2px"
              >
                Select
              </Button>
              <Button
                colorScheme="aurora"
                onClick={() => onRecalculate()}
                isLoading={isRecalculationg}
              >
                Recalculate
              </Button>
            </>
          )}
        </Flex>
      </Flex>
      <VerificationReceivableEligibilityTable
        receivableEligibility={filteredReceivableEligibilityDifferencesForLp}
        eligibilityFormulasAndBasefields={
          eligibilityFormulasAndBasefieldsForSelectedLoanProduct
        }
        selectedLoanProductId={selectedLoanProductId}
        isWaiveDisabled={!showWaiveReceivableEligibilityActions}
        showSelectMode={showSelectMode}
        onExitSelectMode={() => setShowSelectMode(false)}
        creditFacilityId={creditFacilityId}
        reportingDate={reportingDate}
        loanProductReceivableIdGroups={
          receivableIdGroupsByLoanProduct[selectedLoanProductId] ?? []
        }
        receivableWaiversForLoanProduct={
          receivableWaiversByLoanProduct[selectedLoanProductId] ?? []
        }
        borrowerOrganizationName={borrowerOrganizationName}
      />
    </Box>
  );
};
